import type { RadioGroupProps } from '@ksyos/design-system/dist/components/radio-group';
import type { ChangeEvent } from 'react';
import { Choicebox, Radio, RadioButton, RadioGroup } from '@ksyos/design-system';

type type = 'choicebox' | 'radio' | 'radioButton';

export interface BooleanRadioGroupProps extends Omit<RadioGroupProps, 'children' | 'onChange' | 'value'> {
    automation?: string;
    labelFalse?: string;
    labelTrue?: string;
    onChange: (value: boolean | null) => void;
    type: type;
    value: boolean | null;
}

export function BooleanRadioGroup({ ...props }: BooleanRadioGroupProps) {
    function handleChange(event: ChangeEvent<HTMLInputElement>) {
        if (!event?.target?.value) {
            props.onChange(null);
        } else {
            const value: boolean = event.target.value === 'true';
            props.onChange(value);
        }
    }

    function renderChildren(type: type): RadioGroupProps['children'] {
        const childPropsList = [
            {
                key: 0,
                label: props?.labelTrue ?? 'Ja',
                value: 'true',
                className: props.automation ? `automation-${props.automation}-yes` : '',
            },
            {
                key: 1,
                label: props?.labelFalse ?? 'Nee',
                value: 'false',
                className: props.automation ? `automation-${props.automation}-no` : '',
            },
        ];

        switch (type) {
            case 'choicebox':
                return childPropsList.map((childProps) => (
                    <Choicebox
                        {...childProps}
                        key={childProps.key}
                    />
                ));
            case 'radio':
                return childPropsList.map((childProps) => (
                    <Radio
                        {...childProps}
                        key={childProps.key}
                    />
                ));
            case 'radioButton':
                return childPropsList.map((childProps) => (
                    <RadioButton
                        {...childProps}
                        key={childProps.key}
                    />
                ));
            default:
                throw new Error(`Unexpected type: ${type}`);
        }
    }

    return (
        <RadioGroup
            {...props}
            layout={props.layout ?? 'row'}
            onChange={handleChange}
            value={props.value === null ? '' : Boolean(props.value).toString()}
        >
            {renderChildren(props.type)}
        </RadioGroup>
    );
}
